import { Link } from 'react-router-dom'

import {
  CompanyMonogram,
  CompanySpine,
  DueDateBadge,
  StatusPill,
  UserAvatar,
} from '@/shared/components/common'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { CompanySummary, ProjectSummary } from '@/shared/types/domain'

export interface PortfolioCardsProps {
  projects: readonly ProjectSummary[]
  className?: string
}

interface CompanyGroup {
  company: CompanySummary
  projects: ProjectSummary[]
  overdue: number
  open: number
}

/** Agrupa por empresa y ordena por vencidas desc: primero lo que duele. */
function groupByCompany(projects: readonly ProjectSummary[]): CompanyGroup[] {
  const groups = new Map<string, CompanyGroup>()

  for (const project of projects) {
    const group = groups.get(project.company.id) ?? {
      company: project.company,
      projects: [],
      overdue: 0,
      open: 0,
    }
    group.projects.push(project)
    group.overdue += project.overdue_tasks
    group.open += project.open_tasks
    groups.set(project.company.id, group)
  }

  return [...groups.values()].sort(
    (a, b) => b.overdue - a.overdue || a.company.name.localeCompare(b.company.name),
  )
}

export function PortfolioCards({ projects, className }: PortfolioCardsProps) {
  const t = useT()
  const groups = groupByCompany(projects)

  return (
    <div className={cn('grid gap-3 lg:grid-cols-2 xl:grid-cols-3', className)}>
      {groups.map((group) => (
        <section
          key={group.company.id}
          className="hairline flex flex-col overflow-hidden rounded-[var(--radius-card)] bg-surface-1"
        >
          <CompanySpine
            hue={group.company.color_hue}
            orientation="top"
            label={group.company.name}
          />

          <header className="hairline-b flex items-center gap-2 px-3 py-2">
            <CompanyMonogram name={group.company.name} hue={group.company.color_hue} size="md" />
            <Link
              to={`/companies/${group.company.id}`}
              className="min-w-0 flex-1 truncate font-display text-base hover:text-signal"
            >
              {group.company.name}
            </Link>
            {group.overdue > 0 ? (
              <span className="num text-xs font-medium text-overdue">
                <span aria-hidden="true">! </span>
                {t('portfolio.overdueTasks', { count: group.overdue })}
              </span>
            ) : (
              <span className="num text-2xs text-text-3">
                {t('portfolio.openTasks', { count: group.open })}
              </span>
            )}
          </header>

          <ul className="flex flex-col">
            {group.projects.map((project) => (
              <li key={project.id} className="hairline-b last:border-b-0">
                <Link
                  to={`/projects/${project.id}`}
                  className="flex flex-col gap-1 px-3 py-2 hover:bg-surface-2/60"
                >
                  <div className="flex items-center gap-2">
                    <StatusPill category={project.stage.category}>{project.stage.name}</StatusPill>
                    {project.is_ongoing ? (
                      <StatusPill tone="ongoing" variant="quiet">
                        {t('portfolio.ongoing')}
                      </StatusPill>
                    ) : null}
                    <span className="flex-1" />
                    <DueDateBadge date={project.next_due_date} />
                  </div>

                  <div className="flex items-center gap-2">
                    {project.is_ongoing ? null : (
                      <span className="num w-5 shrink-0 text-2xs text-text-3">
                        {project.position}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate text-xs text-text-1">
                      {project.name}
                    </span>
                    <UserAvatar user={project.internal_owner} />
                  </div>

                  <div className="num flex items-center gap-3 text-2xs text-text-3">
                    <span>{t('portfolio.openTasks', { count: project.open_tasks })}</span>
                    <span className={cn(project.overdue_tasks > 0 && 'font-medium text-overdue')}>
                      {project.overdue_tasks > 0 ? '! ' : ''}
                      {t('portfolio.overdueTasks', { count: project.overdue_tasks })}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
